import { useEffect, useMemo, useState } from "react";
import { course, getModule, getRequiredModuleIds } from "./content/course";
import { AssessmentPage } from "./components/AssessmentPage";
import { CompletionPage } from "./components/CompletionPage";
import { CourseLayout } from "./components/CourseLayout";
import { Landing } from "./components/Landing";
import { ModulePage } from "./components/ModulePage";
import { isActivityReady } from "./lib/activity";
import {
  loadProgress,
  markModuleComplete,
  resetProgress,
  saveProgress,
  setActivityState,
  type CourseProgress,
} from "./lib/progress";

type Screen =
  | { kind: "landing" }
  | { kind: "module"; moduleId: string }
  | { kind: "assessment" }
  | { kind: "complete" };

function firstScreen(progress: CourseProgress): Screen {
  if (!progress.started) return { kind: "landing" };
  if (progress.passedAt) return { kind: "complete" };
  const required = getRequiredModuleIds();
  const next = required.find(
    (moduleId) => !progress.completedModuleIds.includes(moduleId),
  );
  return next ? { kind: "module", moduleId: next } : { kind: "assessment" };
}

export function App() {
  const loaded = useMemo(() => loadProgress(window.localStorage), []);
  const [progress, setProgress] = useState<CourseProgress>(loaded.progress);
  const storageAvailable = loaded.storageAvailable;
  const [screen, setScreen] = useState<Screen>(() => firstScreen(loaded.progress));

  useEffect(() => {
    saveProgress(window.localStorage, progress);
  }, [progress]);

  const startCourse = () => {
    const next: CourseProgress = {
      ...progress,
      started: true,
    };
    setProgress(next);
    setScreen(firstScreen(next));
  };

  if (screen.kind === "landing" || !progress.started) {
    return (
      <Landing
        onStart={startCourse}
        hasSavedProgress={progress.started}
      />
    );
  }

  const requiredModuleIds = getRequiredModuleIds();

  const returnHome = () => setScreen({ kind: "landing" });
  const reset = () => {
    const next = resetProgress();
    setProgress(next);
    setScreen({ kind: "landing" });
  };

  if (screen.kind === "complete" && progress.passedAt) {
    return (
      <CompletionPage
        passedAt={progress.passedAt}
        attempts={progress.assessmentAttempts}
        onExploreRoutes={returnHome}
      />
    );
  }

  const currentModuleId =
    screen.kind === "module" ? screen.moduleId : undefined;

  return (
    <CourseLayout
      requiredModuleIds={requiredModuleIds}
      completedModuleIds={progress.completedModuleIds}
      currentModuleId={currentModuleId}
      assessmentActive={screen.kind === "assessment"}
      storageAvailable={storageAvailable}
      onNavigate={(moduleId) => setScreen({ kind: "module", moduleId })}
      onHome={returnHome}
      onReset={reset}
    >
      {screen.kind === "assessment" ? (
        <AssessmentPage
          key={progress.assessmentSeed}
          seed={progress.assessmentSeed}
          onBack={() =>
            setScreen({ kind: "module", moduleId: course.moduleIds.at(-1)! })
          }
          onSubmit={(score, questionIds) => {
            const attempt = {
              attemptedAt: new Date().toISOString(),
              score,
              questionIds,
            };
            if (score >= course.passPercentage) {
              const next = {
                ...progress,
                assessmentAttempts: [...progress.assessmentAttempts, attempt],
                passedAt: new Date().toISOString(),
              };
              setProgress(next);
              setScreen({ kind: "complete" });
            } else {
              setProgress({
                ...progress,
                assessmentAttempts: [...progress.assessmentAttempts, attempt],
                assessmentSeed: progress.assessmentSeed + 1,
              });
            }
          }}
        />
      ) : screen.kind === "module" ? (
        <ModulePage
          module={getModule(screen.moduleId)}
          activityState={progress.activities[screen.moduleId]}
          isCompleted={progress.completedModuleIds.includes(screen.moduleId)}
          canComplete={
            !getModule(screen.moduleId).activity ||
            isActivityReady(
              getModule(screen.moduleId),
              progress.activities[screen.moduleId],
            )
          }
          onActivityChange={(state) =>
            setProgress((current) =>
              setActivityState(current, screen.moduleId, state),
            )
          }
          onBack={
            requiredModuleIds.indexOf(screen.moduleId) > 0
              ? () =>
                  setScreen({
                    kind: "module",
                    moduleId:
                      requiredModuleIds[
                        requiredModuleIds.indexOf(screen.moduleId) - 1
                      ],
                  })
              : undefined
          }
          finalModule={
            screen.moduleId === requiredModuleIds.at(-1)
          }
          onComplete={() => {
            const moduleIndex = requiredModuleIds.indexOf(screen.moduleId);
            const nextProgress = markModuleComplete(progress, screen.moduleId);
            setProgress(nextProgress);
            const nextModuleId = requiredModuleIds[moduleIndex + 1];
            setScreen(
              nextModuleId
                ? { kind: "module", moduleId: nextModuleId }
                : { kind: "assessment" },
            );
          }}
        />
      ) : null}
    </CourseLayout>
  );
}
